import { useRoute } from 'nuxt/app'
import { resolveLocalAuthCookieUser, resolveRestoredAuthUser } from '../utils/auth-cookie'
import { resolveDefaultLoginRedirectUrl, resolveLoginRedirectUrl } from '../utils/login-redirect'

const YEAR_SECONDS = 60 * 60 * 24 * 365
const AUTH_COOKIE_NAMES = ['DJkdikKMG', 'ViLartidlakd', 'WsdsfgDGdagYms'] as const

const extractCookieValue = (response: any) =>
  response?.result?.Value ||
  response?.data?.Value ||
  response?.usercenter?.getcookie?.result?.Value ||
  response?.usercenter?.getcookie?.data?.Value ||
  response?.usercenter?.getcookie?.Value ||
  ''

const extractUserInfo = (response: any) =>
  response?.result ||
  response?.data ||
  response?.usercenter?.getuserfromcookie?.result ||
  response?.usercenter?.getuserfromcookie?.data ||
  null

const postToParent = (payload: Record<string, any>) => {
  if (!import.meta.client) {
    return
  }

  window.parent?.postMessage(payload, '*')
}

const getCookieSecurityOptions = () => {
  if (!import.meta.client || window.location.protocol !== 'https:') {
    return {}
  }

  return {
    sameSite: 'none' as const,
    secure: true,
  }
}

const setLoginTokenCookies = (userInfo: any) => {
  const loginToken = userInfo?.LoginToken || userInfo

  if (!loginToken?.expires_in || !loginToken?.access_token) {
    return
  }

  const maxAge = Number(loginToken.expires_in)
  useAppCookie('logintoken', { maxAge }).value = loginToken.access_token
  useAppCookie('access_token', { maxAge }).value = loginToken.access_token
  useAppCookie('refresh_token', { maxAge }).value = loginToken.refresh_token || ''
  useAppCookie('refreshtime', { maxAge }).value = String(Date.now())
}

export const useLogin = () => {
  const appStore = useAppStore()
  const route = useRoute()
  const { getUserCookie, getUserFromCookie, invokeV3 } = useLoginApi()

  const resolveAuthCookieName = () => {
    if (appStore.isWiki === '1') return 'DJkdikKMG'
    if (appStore.isWiki === '2') return 'ViLartidlakd'
    return 'WsdsfgDGdagYms'
  }

  const readAuthCookieValue = () => {
    const preferredCookieName = resolveAuthCookieName()
    const cookieNames = [preferredCookieName, ...AUTH_COOKIE_NAMES.filter((name) => name !== preferredCookieName)]

    for (const cookieName of cookieNames) {
      const cookie = useAppCookie(cookieName)
      if (typeof cookie.value === 'string' && cookie.value) {
        return cookie.value
      }
    }

    return ''
  }

  const fetchAuthCookieValue = async (userInfo: Record<string, any>) => {
    const directResponse = await getUserCookie(userInfo).catch(() => null)
    const directCookie = extractCookieValue(directResponse)
    if (directCookie) {
      return directCookie
    }

    const invokeResponse = await invokeV3('usercenter$getcookie', userInfo).catch(() => null)
    return extractCookieValue(invokeResponse)
  }

  const fetchUserInfoFromCookie = async (cookieValue: string) => {
    if (!cookieValue) {
      return null
    }

    const response = await getUserFromCookie({ contentcookie: cookieValue }).catch(() => null)
    return extractUserInfo(response)
  }

  const writeAuthCookie = (cookieValue: string) => {
    const cookieName = resolveAuthCookieName()
    const securityOptions = getCookieSecurityOptions()

    clearAppCookies(AUTH_COOKIE_NAMES.filter((name) => name !== cookieName))

    useAppCookie(cookieName, {
      maxAge: YEAR_SECONDS,
      ...securityOptions,
    }).value = cookieValue
    appStore.setUsercookie(cookieValue)
  }

  const clearLoginCookies = () => {
    clearAppCookies([
      ...AUTH_COOKIE_NAMES,
      'userid',
      'logintoken',
      'access_token',
      'refresh_token',
      'refreshtime',
      'userbaseinfo',
      'nickavatar',
    ])
    if (import.meta.client) {
      window.localStorage.removeItem('shared-app-user')
    }
    appStore.setUsercookie('')
    appStore.setUser({})
  }

  const afterLoginSuccess = (cookieValue: string) => {
    appStore.setLoginShow(false)
    postToParent({ op_type: 'reload', url: '', parm: cookieValue })

    if (import.meta.client && String(route.name || '').startsWith('userlogin') && !appStore.userlogin2) {
      const redirectUrl = resolveLoginRedirectUrl(
        route.query.returnurl,
        resolveDefaultLoginRedirectUrl(appStore.LanguageCode || 'en'),
        window.location.origin,
      )
      window.setTimeout(() => {
        window.location.href = redirectUrl
      }, 1200)
    }
  }

  const setCookieFromThirdParty = async (cookieValue: string, userInfo: Record<string, any> = {}) => {
    if (userInfo.userId || userInfo.nick || userInfo.avatar) {
      appStore.setUser(userInfo)
    }

    writeAuthCookie(cookieValue)
    const userFromCookie = await fetchUserInfoFromCookie(cookieValue)
    if (userFromCookie) {
      appStore.setUser({
        userId: userFromCookie.userId || '',
        nick: userFromCookie.nick || userFromCookie.nickName || '',
        avatar: userFromCookie.avatar || userFromCookie.profilePhoto || '',
      })
    }
    afterLoginSuccess(cookieValue)
  }

  const restoreLoginState = async () => {
    if (appStore.contentcookie && appStore.user?.userId) return

    const cookieValue = appStore.contentcookie || readAuthCookieValue()

    if (!cookieValue) return

    const storedUserId = useAppCookie('userid').value || ''
    const localCookieUser = resolveLocalAuthCookieUser(cookieValue, storedUserId)
    if (localCookieUser) {
      appStore.setUsercookie(cookieValue)
      appStore.setUser(localCookieUser)
      return
    }

    const userInfo = await fetchUserInfoFromCookie(cookieValue)
    const cookieUser = resolveRestoredAuthUser(cookieValue, storedUserId, {
      userId: userInfo?.userId || '',
      nick: userInfo?.nick || userInfo?.nickName || '',
      avatar: userInfo?.avatar || userInfo?.profilePhoto || '',
    })
    appStore.setUsercookie(cookieValue)
    if (cookieUser) {
      appStore.setUser(cookieUser)
    }
  }

  const accessCookie = async (userInfo: any) => {
    const mergedUser = {
      userId: userInfo?.userId || '',
      nick: userInfo?.showNickName || userInfo?.nick || userInfo?.nickName || '',
      avatar: userInfo?.showAvataraddress || userInfo?.avatar || userInfo?.profilePhoto || '',
    }

    appStore.setUser(mergedUser)
    appStore.setLoginverify(false)
    setLoginTokenCookies(userInfo)

    const cookieValue = await fetchAuthCookieValue(mergedUser)

    if (cookieValue) {
      writeAuthCookie(cookieValue)
      afterLoginSuccess(cookieValue)
    }

    return cookieValue
  }

  return {
    accessCookie,
    clearLoginCookies,
    restoreLoginState,
    setLoginTokenCookies,
    setCookieFromThirdParty,
    writeAuthCookie,
  }
}
